import React, { useRef, useState } from 'react';
import { FieldValues, FieldName } from 'react-hook-form';
import { ChromePicker } from 'react-color';
import Popper from '@mui/material/Popper';
import classNames from 'classnames';
import { TextField, TextFieldProps } from '../TextField/TextField';
import styles from './styles.module.scss';

type Props<T extends FieldValues> = TextFieldProps<T> & {
  mask?: undefined;
  onChange?: (newValue: string) => void;
};

export const ColorPicker = <T extends FieldValues>({
  className,
  onChange,
  ...props
}: Props<T>) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  // Any is used for now til an adequate type for color is found. @types/color did not work. Possibly an update as color options such as "blue" are not required for css properties like background color and this broke things.
  const color =
    props.value ?? (props.form?.watch(props.name) as string | undefined | any);

  const [textfieldEl, setTextfieldEl] = useState<Element | null>(null);
  const popperEl = useRef<HTMLDivElement | null>(null);

  function openPopper(e: React.FocusEvent) {
    setTextfieldEl(e.target);
    setShowColorPicker(true);
  }

  function closePopperIfFocusLost(e: React.FocusEvent) {
    const newFocus = e.relatedTarget as Node | null;
    const focusInPopper = !!popperEl.current?.contains(newFocus);
    const focusInAnchor = !!textfieldEl?.contains(newFocus);

    if (!focusInAnchor && !focusInPopper) {
      setShowColorPicker(false);
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.colorPreview} style={{ backgroundColor: color }} />
      <TextField
        {...props}
        className={classNames(
          className,
          styles.colorPickerTextField,
          'colorPickerTextField',
        )}
        mask="#******"
        showFocusStyle={showColorPicker}
        onFocus={openPopper}
        onBlur={closePopperIfFocusLost}
      />

      <Popper
        open={showColorPicker}
        className={styles.colorPickerPopover}
        placement="bottom-start"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: () => [0, -7],
            },
          },
        ]}
        anchorEl={textfieldEl}
        ref={popperEl}
        tabIndex={-1}
        onBlur={closePopperIfFocusLost}
      >
        <ChromePicker
          color={color}
          onChange={(newColor) => {
            props.form?.setValue(
              props.name as FieldName<T>,
              newColor.hex as any,
            );
            onChange?.(newColor.hex);
          }}
          disableAlpha
        />
      </Popper>
    </div>
  );
};
